/**
 * usePwaInstall — estado de instalação do PWA + prompt nativo do Android.
 *
 * Devolve tudo que a UI precisa pra decidir se mostra o aviso de instalação
 * (badge no header) e qual passo a passo exibir. Ver utils/pwa.js pro porquê
 * de cada detecção e InstallPwaModal.jsx pro conteúdo.
 */
import { useCallback, useEffect, useState } from "react";
import { detectBrowser, detectPlatform, installUnlocksPush, isStandalone, logPwaEvent, pushSupported } from "../utils/pwa";

export function usePwaInstall() {
  const [standalone, setStandalone] = useState(() => isStandalone());
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform] = useState(() => detectPlatform());
  const [browser] = useState(() => detectBrowser());

  useEffect(() => {
    // Android/Chrome dispara este evento quando o app é instalável. Guardamos
    // pra poder abrir o diálogo nativo no clique do aluno (a spec exige que
    // o prompt parta de um gesto do usuário, então não dá pra chamar aqui).
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setStandalone(true);
      // R370: fim do funil — só dispara em quem de fato completou a
      // instalação (Android; iOS não emite este evento).
      logPwaEvent("app_installed", { platform, browser });
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // O display-mode muda sem reload quando o aluno abre pelo ícone da tela
    // inicial — sem este listener o badge continuaria aparecendo até um F5.
    let mql;
    const onDisplayModeChange = () => setStandalone(isStandalone());
    try {
      mql = window.matchMedia("(display-mode: standalone)");
      if (mql.addEventListener) mql.addEventListener("change", onDisplayModeChange);
      else if (mql.addListener) mql.addListener(onDisplayModeChange);
    } catch (_) { /* navegador sem matchMedia — ignora */ }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (mql) {
        if (mql.removeEventListener) mql.removeEventListener("change", onDisplayModeChange);
        else if (mql.removeListener) mql.removeListener(onDisplayModeChange);
      }
    };
    // platform/browser nunca mudam depois do mount (useState com initializer,
    // sem setter usado) — inclusos só pra satisfazer o linter honestamente,
    // não geram re-registro do listener em nenhum caso real.
  }, [platform, browser]);

  /** Abre o diálogo nativo de instalação (só Android/Chrome). */
  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return { ok: false, reason: "unavailable" };
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      const accepted = choice?.outcome === "accepted";
      // R370: "accepted" aqui só confirma que o aluno tocou em instalar no
      // diálogo nativo — o evento appinstalled (onInstalled acima) é quem
      // confirma que terminou de verdade.
      logPwaEvent(accepted ? "install_prompt_accepted" : "install_prompt_declined", { platform, browser });
      return { ok: accepted, reason: choice?.outcome };
    } catch (_) {
      return { ok: false, reason: "error" };
    }
  }, [deferredPrompt, platform, browser]);

  return {
    isStandalone: standalone,
    platform,                       // "ios" | "android" | "other"
    browser,
    canPromptInstall: !!deferredPrompt,
    promptInstall,
    // Push disponível agora, na janela atual (no iOS: só quando instalado).
    pushSupported: pushSupported(),
    // Deve convidar a instalar? Usa installUnlocksPush em vez de
    // pushSupported justamente porque no iOS as APIs de push só aparecem
    // depois da instalação — ver utils/pwa.js.
    shouldNudgeInstall: !standalone && installUnlocksPush(),
  };
}

export default usePwaInstall;
